import React, { useState, useEffect, useCallback } from 'react';
import { Handshake, Plus, Trash2, CheckCircle, X, Eye } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import {
  PageHeader, Table, Th, Td, TrHover, Badge, Modal, FormField, Input,
  Select, TextArea, Button, LoadingSpinner, EmptyState, ConfirmDialog,
} from '../components/UI';

const STATUS_BADGE = {
  EM_ANDAMENTO: { label: 'Em andamento', cor: colors.warning },
  ACEITO:       { label: 'Aceito',       cor: colors.success },
  RECUSADO:     { label: 'Recusado',     cor: colors.danger },
  QUITADO:      { label: 'Quitado',      cor: colors.info },
  CANCELADO:    { label: 'Cancelado',    cor: colors.textMuted },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function CriseNegociacoesAgregadasPage() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNovaOpen, setModalNovaOpen] = useState(false);
  const [detalheId, setDetalheId] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/negociacoes-agregadas`, { headers: authHeaders() })
      .then(r => r.json()).then(setLista).catch(console.error)
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!detalheId) { setDetalhe(null); return; }
    fetch(`${API}/negociacoes-agregadas/${detalheId}`, { headers: authHeaders() })
      .then(r => r.json()).then(setDetalhe).catch(console.error);
  }, [detalheId]);

  const aceitar = async (id) => {
    if (!window.confirm('Aceitar acordo? Isso vai gerar as parcelas em Contas a Pagar.')) return;
    try {
      const r = await fetch(`${API}/negociacoes-agregadas/${id}/aceitar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: '{}',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      alert(`✓ ${d.parcelas_geradas} parcela(s) geradas em Contas a Pagar.`);
      setDetalheId(id); load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const mudarStatus = async (id, status) => {
    if (!window.confirm(`Mudar status para ${status}?`)) return;
    try {
      const r = await fetch(`${API}/negociacoes-agregadas/${id}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      load(); if (detalheId === id) setDetalheId(id);
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const handleDelete = async () => {
    try {
      const r = await fetch(`${API}/negociacoes-agregadas/${deleteId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setDeleteId(null); load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Negociações Agregadas" subtitle={`${lista.length} negociação(ões)`}
        onAdd={() => setModalNovaOpen(true)} addLabel="Nova Negociação" />

      {lista.length === 0 ? (
        <EmptyState message="Nenhuma negociação agregada cadastrada" icon={Handshake} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Data</Th>
            <Th>Banco</Th>
            <Th>Titular</Th>
            <Th align="center">Cartões</Th>
            <Th>Valor Original</Th>
            <Th>Valor Acordo</Th>
            <Th align="center">Desconto</Th>
            <Th align="center">Parcelas</Th>
            <Th>Status</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {lista.map(n => {
              const sb = STATUS_BADGE[n.status] || { label: n.status, cor: colors.textMuted };
              return (
                <TrHover key={n.id}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{n.data_negociacao}</Td>
                  <Td>{n.banco_nome || '-'}</Td>
                  <Td style={{ fontWeight: 600 }}>{n.titular_nome || '-'}</Td>
                  <Td style={{ textAlign: 'center' }}>{n.qtd_cartoes}</Td>
                  <Td style={{ color: colors.danger }}>{fmt(Number(n.valor_original_total) || 0)}</Td>
                  <Td style={{ color: colors.success, fontWeight: 600 }}>{fmt(Number(n.valor_acordo_total) || 0)}</Td>
                  <Td style={{ textAlign: 'center' }}>{Number(n.desconto_pct || 0).toFixed(1)}%</Td>
                  <Td style={{ textAlign: 'center' }}>
                    {n.qtd_parcelas > 0 ? `${n.parcelas_pagas}/${n.qtd_parcelas}` : '—'}
                  </Td>
                  <Td><Badge text={sb.label} color={sb.cor} /></Td>
                  <Td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button size="sm" variant="outline" color={colors.info} icon={Eye}
                        onClick={() => setDetalheId(n.id)}>Ver</Button>
                      {n.status === 'EM_ANDAMENTO' && (
                        <Button size="sm" color={colors.success} icon={CheckCircle}
                          onClick={() => aceitar(n.id)}>Aceitar</Button>
                      )}
                      {(n.status === 'EM_ANDAMENTO' || n.status === 'ACEITO') && (
                        <Button size="sm" variant="outline" color={colors.danger}
                          onClick={() => setDeleteId(n.id)}>Excluir</Button>
                      )}
                    </div>
                  </Td>
                </TrHover>
              );
            })}
          </tbody>
        </Table>
      )}

      {modalNovaOpen && (
        <NovaNegociacaoModal
          onClose={() => setModalNovaOpen(false)}
          onCreated={(neg) => { setModalNovaOpen(false); load(); setDetalheId(neg.id); }}
        />
      )}

      {detalheId && detalhe && (
        <Modal isOpen={true} onClose={() => setDetalheId(null)} title={`Negociação #${detalhe.id} — ${detalhe.banco_nome || ''} / ${detalhe.titular_nome || ''}`}>
          <DetalheNegociacao
            neg={detalhe}
            onAceitar={() => aceitar(detalhe.id)}
            onMudarStatus={(s) => mudarStatus(detalhe.id, s)}
          />
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Negociação"
        message="Tem certeza? Parcelas pendentes serão removidas; já pagas bloqueiam a exclusão."
      />
    </div>
  );
}

// ============================================================================
// Modal: Nova Negociação
// ============================================================================
function NovaNegociacaoModal({ onClose, onCreated }) {
  const [bancos, setBancos] = useState([]);
  const [titulares, setTitulares] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [form, setForm] = useState({
    banco_id: '', titular_id: '', data_negociacao: todayISO(),
    canal: '', protocolo: '', contato_nome: '',
    valor_acordo_total: '', desconto_pct: '', observacoes: '',
  });
  const [cartoesSel, setCartoesSel] = useState(new Set());
  const [blocos, setBlocos] = useState([
    { quantidade: 6, valor_parcela: '', data_primeira_parcela: '', frequencia_dias: 30 },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/bancos`).then(r => r.json()).then(setBancos).catch(() => {});
    fetch(`${API}/titulares`, { headers: authHeaders() }).then(r => r.json()).then(setTitulares).catch(() => {});
  }, []);

  // Quando banco e titular são escolhidos, carrega cartões em crise daquele par
  useEffect(() => {
    if (!form.banco_id || !form.titular_id) { setCartoes([]); return; }
    fetch(`${API}/cartoes?titular_id=${form.titular_id}&limit=1000`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.data || d.cartoes || []);
        setCartoes(list.filter(c => String(c.banco_id) === String(form.banco_id)));
      })
      .catch(() => setCartoes([]));
    setCartoesSel(new Set());
  }, [form.banco_id, form.titular_id]);

  const toggleCartao = (id) => setCartoesSel(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  const valorOriginalSel = Array.from(cartoesSel).reduce((sum, id) => {
    const c = cartoes.find(x => x.id === id);
    return sum + (Number(c?.fatura_atual) || 0);
  }, 0);

  const totalParcelas = blocos.reduce((s, b) => s + (parseInt(b.quantidade) || 0) * (Number(b.valor_parcela) || 0), 0);
  const qtdTotalParcelas = blocos.reduce((s, b) => s + (parseInt(b.quantidade) || 0), 0);

  const addBloco = () => setBlocos(prev => [...prev, { quantidade: 12, valor_parcela: '', data_primeira_parcela: '', frequencia_dias: 30 }]);
  const removeBloco = (i) => setBlocos(prev => prev.filter((_, idx) => idx !== i));
  const updBloco = (i, k, v) => setBlocos(prev => prev.map((b, idx) => idx === i ? { ...b, [k]: v } : b));

  const salvar = async () => {
    if (cartoesSel.size === 0) return alert('Selecione ao menos 1 cartão');
    if (blocos.length === 0) return alert('Defina ao menos 1 bloco de parcelas');
    for (let i = 0; i < blocos.length; i++) {
      const b = blocos[i];
      if (!b.quantidade || !b.valor_parcela || !b.data_primeira_parcela)
        return alert(`Bloco ${i + 1}: preencha quantidade, valor e data da 1ª parcela`);
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        banco_id: parseInt(form.banco_id) || null,
        titular_id: parseInt(form.titular_id) || null,
        valor_acordo_total: Number(form.valor_acordo_total) || 0,
        desconto_pct: Number(form.desconto_pct) || 0,
        cartoes: Array.from(cartoesSel).map(id => ({ cartao_id: id })),
        plano: blocos.map(b => ({
          quantidade: parseInt(b.quantidade),
          valor_parcela: Number(b.valor_parcela),
          data_primeira_parcela: b.data_primeira_parcela,
          frequencia_dias: parseInt(b.frequencia_dias) || 30,
        })),
      };
      const r = await fetch(`${API}/negociacoes-agregadas`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erro');
      onCreated(data);
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Nova Negociação Agregada">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <FormField label="Banco" required>
          <Select value={form.banco_id} onChange={e => setForm(f => ({ ...f, banco_id: e.target.value }))}>
            <option value="">— selecionar —</option>
            {bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
          </Select>
        </FormField>
        <FormField label="Titular" required>
          <Select value={form.titular_id} onChange={e => setForm(f => ({ ...f, titular_id: e.target.value }))}>
            <option value="">— selecionar —</option>
            {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </Select>
        </FormField>
        <FormField label="Data da negociação" required>
          <Input type="date" value={form.data_negociacao} onChange={e => setForm(f => ({ ...f, data_negociacao: e.target.value }))} />
        </FormField>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 6 }}>
          Cartões a incluir ({cartoesSel.size} selecionados)
        </div>
        {form.banco_id && form.titular_id ? (
          cartoes.length === 0 ? (
            <div style={{ padding: 16, color: colors.textMuted, fontSize: 13, background: colors.bg, borderRadius: 8 }}>
              Nenhum cartão deste banco/titular.
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
              <Table>
                <thead><tr><Th style={{ width: 40 }}></Th><Th>Cartão</Th><Th align="center">Final</Th><Th>Fatura Atual</Th></tr></thead>
                <tbody>
                  {cartoes.map(c => (
                    <tr key={c.id} style={{ background: cartoesSel.has(c.id) ? `${colors.primary}18` : undefined, cursor: 'pointer' }}
                      onClick={() => toggleCartao(c.id)}>
                      <Td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={cartoesSel.has(c.id)} onChange={() => toggleCartao(c.id)} />
                      </Td>
                      <Td>{c.nome_cartao}</Td>
                      <Td style={{ textAlign: 'center' }}>{c.numeracao_final || '-'}</Td>
                      <Td style={{ color: colors.danger }}>{fmt(Number(c.fatura_atual) || 0)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )
        ) : (
          <div style={{ padding: 16, color: colors.textMuted, fontSize: 13 }}>
            Selecione banco e titular acima pra listar cartões.
          </div>
        )}
        <div style={{ marginTop: 6, fontSize: 13, color: colors.textMuted, textAlign: 'right' }}>
          Soma das faturas selecionadas (valor original): <strong style={{ color: colors.danger }}>{fmt(valorOriginalSel)}</strong>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginTop: 16 }}>
        <FormField label="Valor acordo total">
          <Input type="number" step="0.01" value={form.valor_acordo_total}
            onChange={e => setForm(f => ({ ...f, valor_acordo_total: e.target.value }))} placeholder="0,00" />
        </FormField>
        <FormField label="Desconto %">
          <Input type="number" step="0.01" value={form.desconto_pct}
            onChange={e => setForm(f => ({ ...f, desconto_pct: e.target.value }))} placeholder="0,00" />
        </FormField>
        <FormField label="Protocolo / contato">
          <Input value={form.protocolo} onChange={e => setForm(f => ({ ...f, protocolo: e.target.value }))} placeholder="Protocolo ou nome do contato" />
        </FormField>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
            Plano de Parcelas ({qtdTotalParcelas} parcelas, total {fmt(totalParcelas)})
          </div>
          <Button size="sm" variant="outline" color={colors.primary} icon={Plus} onClick={addBloco}>
            Adicionar bloco
          </Button>
        </div>
        {blocos.map((b, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 100px 40px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <Input type="number" min="1" placeholder="Qtd" value={b.quantidade}
              onChange={e => updBloco(i, 'quantidade', e.target.value)} />
            <Input type="number" step="0.01" placeholder="Valor da parcela" value={b.valor_parcela}
              onChange={e => updBloco(i, 'valor_parcela', e.target.value)} />
            <Input type="date" placeholder="1ª parcela" value={b.data_primeira_parcela}
              onChange={e => updBloco(i, 'data_primeira_parcela', e.target.value)} />
            <Input type="number" placeholder="Freq dias" value={b.frequencia_dias}
              onChange={e => updBloco(i, 'frequencia_dias', e.target.value)} title="Dias entre parcelas (30=mensal)" />
            <button onClick={() => removeBloco(i)} disabled={blocos.length === 1}
              style={{ background: 'none', border: 'none', color: colors.danger, cursor: blocos.length === 1 ? 'not-allowed' : 'pointer', opacity: blocos.length === 1 ? 0.3 : 1 }}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <FormField label="Observações">
        <TextArea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} />
      </FormField>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button variant="outline" color={colors.textMuted} onClick={onClose}>Cancelar</Button>
        <Button onClick={salvar} disabled={saving}>{saving ? 'Salvando…' : 'Criar Negociação'}</Button>
      </div>
    </Modal>
  );
}

// ============================================================================
// Componente: Detalhe
// ============================================================================
function DetalheNegociacao({ neg, onAceitar, onMudarStatus }) {
  const sb = STATUS_BADGE[neg.status] || { label: neg.status, cor: colors.textMuted };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Badge text={sb.label} color={sb.cor} />
        {neg.status === 'EM_ANDAMENTO' && (
          <>
            <Button size="sm" color={colors.success} icon={CheckCircle} onClick={onAceitar}>Aceitar acordo</Button>
            <Button size="sm" variant="outline" color={colors.danger} icon={X} onClick={() => onMudarStatus('RECUSADO')}>Recusar</Button>
            <Button size="sm" variant="outline" color={colors.textMuted} onClick={() => onMudarStatus('CANCELADO')}>Cancelar</Button>
          </>
        )}
        {neg.status === 'ACEITO' && (
          <>
            <Button size="sm" color={colors.info} onClick={() => onMudarStatus('QUITADO')}>Marcar como Quitado</Button>
            <Button size="sm" variant="outline" color={colors.textMuted} onClick={() => onMudarStatus('CANCELADO')}>Cancelar acordo</Button>
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Data" value={neg.data_negociacao} />
        <Stat label="Valor Original" value={fmt(Number(neg.valor_original_total) || 0)} color={colors.danger} />
        <Stat label="Valor Acordo" value={fmt(Number(neg.valor_acordo_total) || 0)} color={colors.success} />
        <Stat label="Desconto" value={`${Number(neg.desconto_pct || 0).toFixed(1)}%`} />
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 700, margin: '8px 0', color: colors.textMuted, textTransform: 'uppercase' }}>Cartões incluídos</h4>
      <Table>
        <thead><tr><Th>Cartão</Th><Th align="center">Final</Th><Th>Fatura</Th><Th>Status Acordo</Th></tr></thead>
        <tbody>
          {(neg.cartoes || []).map(c => (
            <tr key={c.cartao_id}>
              <Td>{c.nome_cartao}</Td>
              <Td style={{ textAlign: 'center' }}>{c.numeracao_final || '-'}</Td>
              <Td>{fmt(Number(c.valor_divida) || 0)}</Td>
              <Td>{c.status_acordo || '-'}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h4 style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 8px', color: colors.textMuted, textTransform: 'uppercase' }}>Plano de Parcelas</h4>
      <Table>
        <thead><tr><Th align="center">#</Th><Th align="center">Qtd</Th><Th>Valor por parcela</Th><Th>1ª parcela</Th><Th align="center">Freq</Th><Th>Subtotal</Th></tr></thead>
        <tbody>
          {(neg.plano || []).map(b => (
            <tr key={b.ordem}>
              <Td style={{ textAlign: 'center' }}>{b.ordem}</Td>
              <Td style={{ textAlign: 'center' }}>{b.quantidade}</Td>
              <Td>{fmt(Number(b.valor_parcela) || 0)}</Td>
              <Td>{b.data_primeira_parcela}</Td>
              <Td style={{ textAlign: 'center' }}>{b.frequencia_dias}d</Td>
              <Td style={{ fontWeight: 600 }}>{fmt((Number(b.valor_parcela) || 0) * b.quantidade)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {(neg.parcelas || []).length > 0 && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 8px', color: colors.textMuted, textTransform: 'uppercase' }}>
            Parcelas geradas em Contas a Pagar
          </h4>
          <Table>
            <thead><tr><Th align="center">#</Th><Th>Vencimento</Th><Th>Valor</Th><Th>Status</Th><Th>Pagamento</Th></tr></thead>
            <tbody>
              {neg.parcelas.map(p => (
                <tr key={p.id}>
                  <Td style={{ textAlign: 'center' }}>{p.parcela_num}/{p.parcela_total}</Td>
                  <Td>{p.data_vencimento}</Td>
                  <Td>{fmt(Number(p.valor) || 0)}</Td>
                  <Td><Badge text={p.status} color={p.status === 'PAGO' ? colors.success : p.status === 'ATRASADO' ? colors.danger : colors.warning} /></Td>
                  <Td>{p.data_pagamento ? `${p.data_pagamento} — ${fmt(Number(p.valor_pago) || 0)}` : '-'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {neg.observacoes && (
        <div style={{ marginTop: 16, padding: 12, background: colors.bg, borderRadius: 8, fontSize: 13, color: colors.textMuted }}>
          <strong>Observações:</strong> {neg.observacoes}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color || colors.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}
